import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Shield, Edit, Save, AlertTriangle, CheckCircle, XCircle,
  Loader2, Bot, Lock, FileText
} from "lucide-react";

const LOCATION_LABELS = {
  global: "All Pages",
  submissions: "Submission Forms",
  enforcement: "Enforcement Module",
  drc: "DRC Module",
  ai_analysis: "AI Analysis Output"
};

const GovernanceNoticesPage = () => {
  const { token, user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNotice, setEditingNotice] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "", is_active: true, requires_acknowledgment: false });
  const [saving, setSaving] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    fetchNotices();
  }, [token]);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/governance-notices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotices(res.data || []);
    } catch {
      toast.error("Failed to load governance notices");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (notice) => {
    setEditingNotice(notice);
    setEditForm({
      title: notice.title,
      content: notice.content,
      is_active: notice.is_active !== false,
      requires_acknowledgment: notice.requires_acknowledgment || false
    });
    setAiReview(null);
  };

  const handleSave = async () => {
    if (!editingNotice) return;
    setSaving(true);
    setAiReview(null);

    try {
      const res = await axios.put(`${API}/governance-notices/${editingNotice.notice_id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.status === "approved") {
        toast.success("Notice updated - AI legal review passed");
        setAiReview(res.data.ai_review);
        setEditingNotice(null);
        fetchNotices();
      } else if (res.data.status === "rejected") {
        toast.error("AI legal review flagged concerns");
        setAiReview(res.data.ai_review);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save notice");
    } finally {
      setSaving(false);
    }
  };

  const handleForceSave = async () => {
    if (!editingNotice || !overrideReason) return;
    setSaving(true);
    try {
      await axios.post(`${API}/governance-notices/${editingNotice.notice_id}/force-save`, {
        ...editForm,
        override_reason: overrideReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notice force-saved with admin override (audit logged)");
      setForceDialogOpen(false);
      setEditingNotice(null);
      setOverrideReason("");
      setAiReview(null);
      fetchNotices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to force-save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      <main className="flex-1 p-6" data-testid="governance-notices-page">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-6 h-6 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold">Governance Notices</h1>
              <p className="text-sm text-muted-foreground">
                Manage legal governance notices displayed throughout the application. All edits are reviewed by AI for legal compliance.
              </p>
            </div>
          </div>

          {/* AI Review Banner */}
          <Card className="mb-6 border-blue-200 bg-blue-50/50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Bot className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">AI Legal Gateway Active</p>
                  <p className="text-xs text-blue-700">All edits are reviewed by AI to ensure they don't create legal concerns, liabilities, or conflicts with HOA governing documents.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              {notices.map((notice) => (
                <Card key={notice.notice_id} data-testid={`notice-card-${notice.notice_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base">{notice.title}</CardTitle>
                        <Badge variant="outline" className="text-xs">{LOCATION_LABELS[notice.location] || notice.location}</Badge>
                        {notice.is_active !== false ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-500">Inactive</Badge>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(notice)} data-testid={`edit-notice-${notice.notice_id}`}>
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    </div>
                    {notice.version && (
                      <CardDescription>Version {notice.version} {notice.last_reviewed_by_ai && " — AI Reviewed"}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-line line-clamp-4">{notice.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Edit Dialog */}
          {editingNotice && (
            <Dialog open={!!editingNotice} onOpenChange={() => { setEditingNotice(null); setAiReview(null); }}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Edit className="w-5 h-5" /> Edit Governance Notice
                  </DialogTitle>
                  <DialogDescription>
                    Changes will be reviewed by AI for legal compliance before saving.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea
                      value={editForm.content}
                      onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                      rows={10}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch checked={editForm.is_active} onCheckedChange={(v) => setEditForm({ ...editForm, is_active: v })} />
                      <Label>Active</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={editForm.requires_acknowledgment} onCheckedChange={(v) => setEditForm({ ...editForm, requires_acknowledgment: v })} />
                      <Label>Require Acknowledgment</Label>
                    </div>
                  </div>

                  {/* AI Review Result */}
                  {aiReview && (
                    <Card className={`border-2 ${aiReview.approved ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                      <CardContent className="py-3 space-y-2">
                        <div className="flex items-center gap-2">
                          {aiReview.approved ? (
                            <><CheckCircle className="w-5 h-5 text-green-600" /><span className="font-medium text-green-700">AI Review: Approved</span></>
                          ) : (
                            <><XCircle className="w-5 h-5 text-red-600" /><span className="font-medium text-red-700">AI Review: Concerns Found</span></>
                          )}
                          <Badge variant="outline">{aiReview.risk_level || "N/A"} risk</Badge>
                        </div>
                        <p className="text-sm">{aiReview.summary}</p>
                        {aiReview.concerns?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-red-700">Concerns:</p>
                            <ul className="text-sm list-disc pl-5 text-red-800 space-y-1">
                              {aiReview.concerns.map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                          </div>
                        )}
                        {aiReview.suggestions?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-amber-700">Suggestions:</p>
                            <ul className="text-sm list-disc pl-5 text-amber-800 space-y-1">
                              {aiReview.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {!aiReview.approved && user?.role === "admin" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 border-red-300 text-red-700"
                            onClick={() => setForceDialogOpen(true)}
                          >
                            <Lock className="w-4 h-4 mr-1" /> Admin Override (Force Save)
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => { setEditingNotice(null); setAiReview(null); }}>Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-notice-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                    Save with AI Review
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Force Save Dialog */}
          <Dialog open={forceDialogOpen} onOpenChange={setForceDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" /> Admin Override
                </DialogTitle>
                <DialogDescription>
                  You are overriding the AI legal review. This action is logged in the audit trail.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Label>Override Reason (Required)</Label>
                <Textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Explain why this override is necessary..."
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setForceDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={handleForceSave}
                  disabled={!overrideReason || saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Force Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};

export default GovernanceNoticesPage;
